import { useCallback, useEffect, useState } from 'react';

import { apiClient } from '../../app/api/client';
import { Card, Page } from '../../app/layout/Page';

import './settings.css';
import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { settingsFieldHelp } from '../../app/guidance/fieldHelpRegistry';

export interface ProviderReadiness {
  browser_origin: string;
  configuration_error: string | null;
  configured: boolean;
  elevation_provider: string;
  guidance: string[];
  live_basemap_available: boolean;
  managed: boolean;
  mutable: boolean;
  open_meteo_terrain_available: boolean;
  source: 'environment' | 'local_file' | null;
  usgs_terrain_available: boolean;
}

export interface SettingsApi {
  clearGoogleMapsKey: () => Promise<void>;
  getProviderReadiness: () => Promise<ProviderReadiness>;
  updateGoogleMapsKey: (apiKey: string) => Promise<ProviderReadiness>;
}

const browserSettingsApi: SettingsApi = {
  clearGoogleMapsKey: () => apiClient.delete('/settings/google-maps'),
  getProviderReadiness: () => apiClient.get('/settings/providers'),
  updateGoogleMapsKey: (apiKey) => apiClient.put('/settings/google-maps', { json: { api_key: apiKey } }),
};

function ownershipLabel(readiness: ProviderReadiness): string {
  if (readiness.managed) return 'Server administrator';
  if (readiness.source === 'local_file') return 'Local settings file';
  return readiness.mutable ? 'Local workspace' : 'Not configured';
}

interface SettingsWorkspaceProps {
  api?: SettingsApi;
}

export function SettingsWorkspace({ api = browserSettingsApi }: SettingsWorkspaceProps) {
  const [readiness, setReadiness] = useState<ProviderReadiness | null>(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setReadiness(await api.getProviderReadiness());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Provider readiness could not be loaded.');
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function save() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setReadiness(await api.updateGoogleMapsKey(key));
      setKey('');
      setNotice('Maps key saved.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The provider key could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.clearGoogleMapsKey();
      setKey('');
      await refresh();
      setNotice('Maps key removed.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The local provider key could not be removed.');
    } finally {
      setBusy(false);
    }
  }

  const mapsStatus = readiness === null ? null : readiness.live_basemap_available ? 'Ready' : readiness.mutable ? 'Needs setup' : 'Unavailable';
  const terrainStatus = readiness === null ? null : readiness.usgs_terrain_available || readiness.open_meteo_terrain_available ? 'Ready' : 'Unavailable';

  return (
    <Page title="Settings">
      {error === null ? null : <p role="alert">{error}</p>}

      <div className="settings-grid">
        <Card title="Maps">
          {readiness === null ? (
            <p role="status">Checking…</p>
          ) : (
            <>
              <p className="settings-status" aria-label={`Maps: ${mapsStatus}`} data-state={mapsStatus?.toLowerCase().replace(' ', '-')} role="status">
                {mapsStatus}
              </p>
              {readiness.configuration_error === null ? null : <p role="alert">{readiness.configuration_error}</p>}
              {!readiness.live_basemap_available && !readiness.mutable ? <p>Ask an administrator to configure Maps.</p> : null}
            </>
          )}

          {readiness?.mutable ? (
            <div className="settings-key-editor">
              <label>
                Maps JavaScript key
                <input
                  {...guided(settingsFieldHelp.mapsKey)}
                  autoComplete="off"
                  maxLength={200}
                  minLength={20}
                  type="password"
                  value={key}
                  onChange={(event) => {
                    setKey(event.currentTarget.value);
                    setNotice(null);
                  }}
                />
              </label>
              <div className="button-row">
                <button className="button button--primary" disabled={busy || key.trim().length < 20} type="button" onClick={() => void save()}>
                  Save key
                </button>
                <button className="button button--secondary" disabled={busy || readiness.source !== 'local_file'} type="button" onClick={() => void clear()}>
                  Remove key
                </button>
              </div>
              {notice === null ? null : <p role="status">{notice}</p>}
            </div>
          ) : null}

          {readiness === null ? null : (
            <details>
              <summary>Map details</summary>
              <dl>
                <div>
                  <dt>Origin</dt>
                  <dd>
                    <code>{readiness.browser_origin}</code>
                  </dd>
                </div>
                <div>
                  <dt>Managed by</dt>
                  <dd>{ownershipLabel(readiness)}</dd>
                </div>
                <div>
                  <dt>Configuration</dt>
                  <dd>{readiness.configured ? 'Configured' : 'Not configured'}</dd>
                </div>
              </dl>
              {readiness.guidance.length === 0 ? null : (
                <ul>
                  {readiness.guidance.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              <p>Keys are never shown or exported.</p>
            </details>
          )}
        </Card>

        <Card title="Terrain">
          {readiness === null ? (
            <p role="status">Checking…</p>
          ) : (
            <>
              <p className="settings-status" aria-label={`Terrain: ${terrainStatus}`} data-state={terrainStatus?.toLowerCase()} role="status">
                {terrainStatus}
              </p>
              <dl>
                <div>
                  <dt>U.S. terrain</dt>
                  <dd>{readiness.usgs_terrain_available ? 'Ready' : 'Unavailable'}</dd>
                </div>
                <div>
                  <dt>Global terrain</dt>
                  <dd>{readiness.open_meteo_terrain_available ? 'Ready' : 'Unavailable'}</dd>
                </div>
              </dl>
              <details>
                <summary>Terrain details</summary>
                <p>{readiness.elevation_provider}</p>
                <p>Datums must match. Google Elevation is not used.</p>
              </details>
            </>
          )}
        </Card>
      </div>
    </Page>
  );
}
